import { forwardRef, type ComponentPropsWithoutRef } from 'react'
import { cn } from '@/lib/utils'
import { indicatorPalette, type IndicatorKind } from './indicator-colors'

export type { IndicatorKind } from './indicator-colors'

const sizeMap = {
  sm: { pill: 'size-3.5', icon: 'size-3.5' },
  md: { pill: 'size-4', icon: 'size-4' },
  lg: { pill: 'size-5', icon: 'size-5' },
} as const

export interface IndicatorIconProps extends Omit<ComponentPropsWithoutRef<'span'>, 'children'> {
  kind: IndicatorKind
  size?: keyof typeof sizeMap
  /** Render in muted/inactive style — faded icon. */
  muted?: boolean
  /** Use the canonical "standard" treatment (opacity-70 on the colored icon). */
  standard?: boolean
  /**
   * Inherit icon color from the surrounding `currentColor` — for use inside
   * buttons that already encode active/inactive state via their own
   * `text-{color}-600` / `text-muted-foreground` classes.
   */
  inherit?: boolean
  iconClassName?: string
}

export const IndicatorIcon = forwardRef<HTMLSpanElement, IndicatorIconProps>(function IndicatorIcon(
  { kind, size = 'sm', muted = false, standard = false, inherit = false, className, iconClassName, ...rest },
  ref,
) {
  const config = indicatorPalette[kind]
  const { pill, icon: iconSize } = sizeMap[size]
  const Icon = config.icon

  const iconColor = inherit ? 'text-current' : muted ? 'text-muted-foreground/40' : config.activeText

  return (
    <span
      ref={ref}
      {...rest}
      className={cn(
        'inline-flex shrink-0 items-center justify-center',
        pill,
        !inherit && !muted && standard && 'opacity-70',
        className,
      )}
    >
      <Icon className={cn(iconSize, 'shrink-0', iconColor, iconClassName)} />
    </span>
  )
})
